import { Component, computed, effect, inject, input, model, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ValueHistoryType } from "src/models/estate/valuation-history.model";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";

@Component({
    selector: "valuation-history-filter",
    templateUrl: "./valuation-history-filter.component.html",
    styleUrl: "./valuation-history-filter.component.scss",
})
export class ValuationHistoryFilterComponent {
    isCommercial = input<boolean>(false);
    selectedFilters = model<ValueHistoryType[]>([]);

    authService = inject(EvAuthorizationService);

    // Role flags (placeholder logic)
    protected hasEvEstimateRoles = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_Estimate2021)); //i gammel løsning kan man også ha 2014, men den er det bare interne som har.
    protected hasAppraisalRoles = toSignal(this.authService.hasPermission([EVServiceType.Appraisal_Realtor, EVServiceType.Appraisal_Bank], false, false));
    hasCommercialAppraisalRoles = computed(() => false); //todo: fikse roller når det blir aktuelt
    hasNorskTakstValueValidationRole = computed(() => false); //evServiceType.NorskTakstValueValidation

    // Sets used by computed filters

    // Public computed selections
    marketSalesSelected = computed(() => {
        const filters = this.selectedFilters();
        return filters.some((type) => this.marketSaleTypes.has(type));
    });

    appraisalsSelected = computed(() => {
        const filters = this.selectedFilters();
        return filters.some((type) => this.appraisalTypes.has(type));
    });

    publicRegistrationSelected = computed(() => {
        const filters = this.selectedFilters();
        return filters.some((type) => this.publicRegistrationTypes.has(type));
    });

    commercialAppraisalSelected = computed(() => {
        const filters = this.selectedFilters();
        return filters.some((type) => this.commercialAppraisalTypes.has(type));
    });

    evEstimateSelected = computed(() => {
        const filters = this.selectedFilters();
        return filters.some((type) => this.storedEstimateTypes.has(type));
    });

    allSelected = computed(() => {
        const filters = this.selectedFilters();
        const available = this.availableFilters();
        const v = available.every((f) => filters.includes(f));
        return v;
    });
    someSelected = computed(() => {
        const filters = this.selectedFilters();
        const available = this.availableFilters();
        const v = available.some((f) => filters.includes(f));
        return v;
    });

    // Event handlers
    onFilterStoredEvEstimat(e: Event) {
        e.stopPropagation();
        this.toggleFilter(this.evEstimateSelected(), this.storedEstimateTypes);
    }
    onFilterCommercialAppraisal(e: Event) {
        e.stopPropagation();
        this.toggleFilter(this.commercialAppraisalSelected(), this.commercialAppraisalTypes);
    }
    onFilterRegistered(e: Event) {
        e.stopPropagation();
        this.toggleFilter(this.publicRegistrationSelected(), this.publicRegistrationTypes);
    }
    onFilterAppraisal(e: Event) {
        e.stopPropagation();
        this.toggleFilter(this.appraisalsSelected(), this.appraisalTypes);
    }
    onFilterMarketSale(e: Event) {
        e.stopPropagation();
        this.toggleFilter(this.marketSalesSelected(), this.marketSaleTypes);
    }
    onFilterToggleAll(e: Event) {
        e.stopPropagation();
        if (this.allSelected()) {
            // Deselect all
            this.selectedFilters.set([]);
        } else {
            // Select all
            this.selectedFilters.set(this.availableFilters());
        }
    }
    private initialized = signal(false);
    private initEffect = effect(() => {
        if (!this.initialized()) {
            if (this.isCommercial()) {
                this.selectedFilters.set([...this.commercialFilters]);
            } else {
                this.selectedFilters.set([...this.estateFilters].filter((type) => type !== ValueHistoryType.LagretEstimat));
            }
            this.initialized.set(true);
        }
    });

    private toggleFilter(isSelected: boolean, types: Set<ValueHistoryType>): void {
        if (isSelected) {
            // Deselect types
            const newFilters = this.selectedFilters().filter((type) => !types.has(type));
            this.selectedFilters.set(newFilters);
        } else {
            // Select types
            const newFilters = [...this.selectedFilters(), ...Array.from(types)];
            this.selectedFilters.set(newFilters);
        }
    }
    // Data lists (still needed before availableFilters)
    private commercialFilters: ValueHistoryType[] = [
        ValueHistoryType.Tinglysning,
        ValueHistoryType.CommercialAppraisalAgent,
        ValueHistoryType.CommercialAppraisalBank,
        ValueHistoryType.CommercialSale,
    ];

    private estateFilters: ValueHistoryType[] = [
        ValueHistoryType.LagretEstimat,
        ValueHistoryType.Meglersalg,
        ValueHistoryType.Takst,
        ValueHistoryType.ETakst,
        ValueHistoryType.ETakstMegler,
        ValueHistoryType.Tinglysning,
    ];
    private readonly appraisalTypes = new Set<ValueHistoryType>([
        ValueHistoryType.ETakst,
        ValueHistoryType.ETakstMegler,
        ValueHistoryType.Takst,
        ValueHistoryType.CommercialAppraisalAgent,
        ValueHistoryType.CommercialAppraisalBank,
    ]);
    private readonly marketSaleTypes = new Set<ValueHistoryType>([ValueHistoryType.Meglersalg, ValueHistoryType.CommercialSale]);
    private readonly storedEstimateTypes = new Set<ValueHistoryType>([ValueHistoryType.LagretEstimat]);
    private readonly publicRegistrationTypes = new Set<ValueHistoryType>([ValueHistoryType.Tinglysning]);
    private readonly commercialAppraisalTypes = new Set<ValueHistoryType>([
        ValueHistoryType.CommercialAppraisalAgent,
        ValueHistoryType.CommercialAppraisalBank,
    ]);
    private availableFilters = computed((): ValueHistoryType[] => {
        return this.isCommercial() ? this.commercialFilters : this.estateFilters;
    });
}
